import L from '../leaflet-plugins-loader';
import { LeafletService } from '../leaflet.service'
import { TranslateService } from '@ngx-translate/core'
import { WmsChartService } from 'wms-chart-loader'
import { WidgetSettingsObserveService } from '../../../../shared/services/helpers/widget-settings-observe.service'
import { ILeafletInstance } from '../leaflet.interface'
import { IAppPluginWidget } from 'core-client/api'

/**
 * WMS Chart Server Provider
 *
 * Manages WMS chart tile layers with OAuth2 authentication
 *
 * Features:
 * - Static token management (global across all instances)
 * - Non-blocking widget registration
 * - Automatic token updates via broadcast
 * - Invalid token detection via tileerror → notifies backend immediately
 */
export class WmsChartServer extends LeafletService {
  private static readonly ressourceUrl: string = 'https://wms.captn.addix.io'
  private static readonly options = {
    maxZoom: 20,
    minZoom: 2,
    maxNativeZoom: 19,
    layers: 'ENC',
    //CSBOOL: '2183',       // Hex: Chart Boundaries + Deep Soundings + Shallow Sounding + Text + Lights
    CSVALUE: ',,,,,3',    // Set only the display category, all other values are default
    format: 'image/png',
    transparent: false,
    version: '1.3.0',
    crs: L.CRS.EPSG4326,
    token: null,
    attribution: '© <a href="https://www.addix.net">ADDIX</a>',
  } as any

  private static _wmsLayerEnabled: boolean = false
  private static _wmsLayerToken: string | null = null

  constructor(translate: TranslateService, widgetSettingObserve: WidgetSettingsObserveService) {
    super(translate, widgetSettingObserve)
  }

  static get wmsLayerEnabled(): boolean {
    return this._wmsLayerEnabled
  }

  static set wmsLayerEnabled(value: boolean) {
    this._wmsLayerEnabled = value
  }

  static get wmsLayerToken(): string | null {
    return this._wmsLayerToken
  }

  static set wmsLayerToken(value: string | null) {
    this._wmsLayerToken = value
  }

  static isServiceEnabledAndTokenAvailable(serviceEnabled: boolean, token: string | null): boolean {
    return serviceEnabled && token !== null && token !== '';
  }

  /**
   * Register widget and return Promise
   *
   * Handler is called:
   * 1. Immediately if token is cached
   * 2. When backend responds to request
   * 3. When backend broadcasts token update
   * 4. With null when token is detected as invalid (tileerror 401/403)
   */
  static async registerWidget(
    appPluginWidget: IAppPluginWidget,
    dashboardId: string,
    wmsChartService: WmsChartService
  ): Promise<string | null> {
    return new Promise((resolve) => {
      wmsChartService.registerNotificationHandler(
        appPluginWidget._id,
        dashboardId,
        appPluginWidget.class as string,
        (accessToken: string | null) => {
          // Update global token (null = invalid/not ready)
          this.wmsLayerToken = accessToken;
          resolve(accessToken);
        }
      );
    });
  }

  /**
   * Add WMS tile layer to map instance
   *
   * Attaches tileerror listener to detect 401/403 responses
   * When WMS server rejects the token, notifies backend immediately
   * so a fresh token can be fetched and broadcast to all clients
   *
   * @param id - Map identifier
   * @param accessToken - OAuth2 access token
   * @param wmsChartService - Service reference for invalid token notification
   * @param instance - Leaflet instance
   */
  static addWmsLayer(
    id: number | string,
    accessToken: string,
    wmsChartService: WmsChartService,
    instance?: ILeafletInstance
  ): void {
    WmsChartServer.options.token = accessToken
    const wmsLayer = L.tileLayer.wms(WmsChartServer.ressourceUrl, WmsChartServer.options);

    // Detect tile load errors - 401/403 means token is invalid
    wmsLayer.on('tileerror', (error: any) => {
      const status = error?.tile?.status ?? error?.xhr?.status;

      if (status === 401 || status === 403) {
        console.warn(
          `[WMS Chart] Tile rejected with ${status} - token invalid, notifying backend`
        );
        // Notify backend to immediately fetch a new token
        // Backend will broadcast new token to all clients
        wmsChartService.notifyInvalidToken();
      }
    });

    if (instance) {
      Object.assign(instance.tileLayers, {
        'WMS-Chart': {
          layer: wmsLayer,
          zoom: 13,
          id: `map_${id}`
        }
      })
    }
  }

  /**
   * Add minimap control with WMS layer
   */
  static addMiniMapLayerToInstance(id: number | string, instance?: ILeafletInstance) {
    if (instance?.layerControls.has(`minimap_${id}`)) {
      const miniMapControl = instance?.layerControls.get(`minimap_${id}`)
      if (miniMapControl) {
        instance.map.removeControl(miniMapControl)
      }
      instance.layerControls.delete(`minimap_${id}`)
    }

    if (instance) {
      const minimMapTileLayer = L.tileLayer.wms(WmsChartServer.ressourceUrl, WmsChartServer.options)

      const miniMap = new L.Control.MiniMap(minimMapTileLayer, {
        width: 215,
        height: 215,
        position: 'topright',
        toggleDisplay: true,
        minimized: true,
        zoomLevelOffset: -5
      }) as any

      miniMap.on('restore', () => {
        miniMap._miniMap.invalidateSize();
      });

      instance?.layerControls.set(`minimap_${id}`, miniMap);
      miniMap.addTo(instance?.map)
    }
  }

  /**
   * Get flags and conditionally fetch token
   *
   * Only registers widget if:
   * - WMS is enabled
   * - WMS layer is selected
   * - Token not already cached
   */
  static async getFlagsAndSetToken(
    appPluginWidget: IAppPluginWidget,
    dashboardId: string,
    wmsChartService: WmsChartService,
    baseTileLayer?: string,
    instance?: ILeafletInstance
  ): Promise<{ addWmsTilerLayer: boolean; wmsLayerEnabled: boolean }> {
    const addWmsTilerLayer =
      (instance?.tileLayers['WMS-Chart']?.layer == undefined && baseTileLayer === 'WMS-Chart') ||
      (instance?.tileLayers['WMS-Chart']?.layer != undefined &&
        !instance.map.hasLayer(instance?.tileLayers['WMS-Chart']?.layer) &&
        baseTileLayer === 'WMS-Chart');

    // Only register if WMS enabled, WMS selected, and no token cached
    if (WmsChartServer.wmsLayerEnabled && WmsChartServer.wmsLayerToken == null && baseTileLayer === 'WMS-Chart') {
      await WmsChartServer.registerWidget(appPluginWidget, dashboardId, wmsChartService);
    }

    const wmsLayerEnabled = WmsChartServer.wmsLayerToken != null;

    return { addWmsTilerLayer, wmsLayerEnabled }
  }
}
